import { AcquisitionTouchContext, EventPayload, IntentProfile, RecommendationAttribution } from "./types";

const PREFIX = "gemsales";
/**
 * Keys written before the GemChat → GemSales rename. Reads fall back to this namespace so a
 * returning shopper keeps their identity, intent profile and buffered events instead of being
 * silently reset to a brand-new visitor by a rename that should be invisible to them.
 */
const LEGACY_PREFIX = "gemchat";

/** Read a namespaced value, preferring the current namespace and falling back to the legacy one. */
function readKey(name: string): string | null {
  return localStorage.getItem(`${PREFIX}:${name}`) ?? localStorage.getItem(`${LEGACY_PREFIX}:${name}`);
}

/**
 * Same, for session-scoped values. A shopper mid-session when the rename ships would otherwise lose
 * their intent profile and — worse — any events buffered but not yet flushed to the collector.
 */
function readSessionKey(name: string): string | null {
  return sessionStorage.getItem(`${PREFIX}:${name}`) ?? sessionStorage.getItem(`${LEGACY_PREFIX}:${name}`);
}

export function getOrCreateId(key: string): string {
  const storageKey = `${PREFIX}:${key}`;
  // A returning shopper's anonymousId lives under the legacy namespace — carry it forward rather
  // than minting a new one, or cross-session memory and win attribution both start from zero.
  const existing = localStorage.getItem(storageKey) ?? localStorage.getItem(`${LEGACY_PREFIX}:${key}`);
  if (existing) {
    if (!localStorage.getItem(storageKey)) localStorage.setItem(storageKey, existing);
    return existing;
  }

  const value = `${key}_${crypto.randomUUID()}`;
  localStorage.setItem(storageKey, value);
  return value;
}

export function getDismissedSlots(): Set<string> {
  const raw = readKey("dismissed_slots");
  if (!raw) return new Set();

  try {
    const values = JSON.parse(raw) as string[];
    return new Set(values);
  } catch {
    return new Set();
  }
}

export function persistDismissedSlots(values: Set<string>): void {
  localStorage.setItem(`${PREFIX}:dismissed_slots`, JSON.stringify([...values]));
}

export function getTriggerImpressionCounts(): Map<string, number> {
  const raw = readSessionKey("trigger_impressions");
  if (!raw) return new Map();

  try {
    const values = JSON.parse(raw) as Array<[string, number]>;
    return new Map(values.filter(([, count]) => Number.isFinite(count)));
  } catch {
    return new Map();
  }
}

export function incrementTriggerImpressionCount(slotId: string, values: Map<string, number>): number {
  const nextCount = (values.get(slotId) ?? 0) + 1;
  values.set(slotId, nextCount);
  sessionStorage.setItem(`${PREFIX}:trigger_impressions`, JSON.stringify([...values]));
  return nextCount;
}

export function getIntentProfile(): IntentProfile {
  const raw = readSessionKey("intent_profile");
  if (!raw) {
    return {
      scores: {},
      topIntent: null,
      updatedAt: null,
    };
  }

  try {
    const parsed = JSON.parse(raw) as IntentProfile;
    return {
      scores: sanitizeScores(parsed.scores),
      topIntent: parsed.topIntent ?? null,
      updatedAt: parsed.updatedAt ?? null,
    };
  } catch {
    return {
      scores: {},
      topIntent: null,
      updatedAt: null,
    };
  }
}

export function persistIntentProfile(profile: IntentProfile): void {
  sessionStorage.setItem(`${PREFIX}:intent_profile`, JSON.stringify(profile));
}

export function getFirstTouchAcquisition(): AcquisitionTouchContext | null {
  const raw = readKey("first_touch_acquisition");
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as AcquisitionTouchContext;
    if (!parsed.landingPage || !parsed.capturedAt) return null;
    return {
      source: parsed.source ?? null,
      medium: parsed.medium ?? null,
      campaign: parsed.campaign ?? null,
      content: parsed.content ?? null,
      term: parsed.term ?? null,
      referrer: parsed.referrer ?? null,
      landingPage: parsed.landingPage,
      capturedAt: parsed.capturedAt,
    };
  } catch {
    return null;
  }
}

export function persistFirstTouchAcquisition(value: AcquisitionTouchContext): void {
  localStorage.setItem(`${PREFIX}:first_touch_acquisition`, JSON.stringify(value));
}

export function persistRecommendationAttribution(attribution: RecommendationAttribution): void {
  sessionStorage.setItem(`${PREFIX}:recommendation_attribution`, JSON.stringify(attribution));
}

export function getRecommendationAttribution(now = Date.now(), windowMs = 30 * 60 * 1000): RecommendationAttribution | null {
  const raw = readSessionKey("recommendation_attribution");
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as RecommendationAttribution;
    if (!parsed.recommendationId || !Number.isFinite(parsed.clickedAt)) return null;
    if (now - parsed.clickedAt > windowMs) return null;
    return {
      recommendationId: parsed.recommendationId,
      flowId: parsed.flowId,
      resultTitle: parsed.resultTitle,
      ctaUrl: parsed.ctaUrl ?? null,
      recommendedProductHandle: parsed.recommendedProductHandle ?? null,
      clickedAt: parsed.clickedAt,
    };
  } catch {
    return null;
  }
}

export function appendBufferedEvent(payload: EventPayload, limit = 250): void {
  const events = getBufferedEvents();
  events.push(payload);
  sessionStorage.setItem(`${PREFIX}:event_buffer`, JSON.stringify(events.slice(-limit)));
}

export function getBufferedEvents(): EventPayload[] {
  const raw = readSessionKey("event_buffer");
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw) as EventPayload[];
    return Array.isArray(parsed) ? parsed.filter(isEventPayloadLike) : [];
  } catch {
    return [];
  }
}

export function clearBufferedEvents(): void {
  sessionStorage.removeItem(`${PREFIX}:event_buffer`);
}

function sanitizeScores(scores: Record<string, number> | undefined): Record<string, number> {
  if (!scores) return {};

  return Object.fromEntries(
    Object.entries(scores).filter(([, score]) => Number.isFinite(score)).map(([intent, score]) => [intent, clamp(score)]),
  );
}

function clamp(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function isEventPayloadLike(value: EventPayload): value is EventPayload {
  return Boolean(value?.eventName && value?.timestamp && value?.shopId && value?.sessionId);
}
